import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Navigation, Smartphone, AlertCircle, CheckCircle } from 'lucide-react';
import { AgentLayout } from '../../components/AgentLayout';
import api from '../../api';

const SERVICE_ICONS = {
  sound_box: '🔊', upi_qr: '📱', current_savings: '🏦',
  daily_account: '📅', fixed_deposit: '📈', recurring_deposit: '🔁',
  monthly_interest: '💵', loan: '💰', other: '📦'
};

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`}>{msg}</div>;
}

export default function EditMerchant() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState(null);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    businessName: '', ownerName: '', phone: '', address: '', city: '',
    nextVisitDate: '', interestedServices: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, msg: '' });
  const [showUPIBanner, setShowUPIBanner] = useState(false);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const loadData = useCallback(async () => {
    try {
      const [merchantRes, servicesRes] = await Promise.all([
        api.get(`/merchants/${id}`),
        api.get('/services')
      ]);
      const m = merchantRes.data;
      setMerchant(m);
      setServices(servicesRes.data || []);
      setForm({
        businessName: m.businessName || '',
        ownerName: m.ownerName || '',
        phone: m.phone || '',
        address: m.address || '',
        city: m.city || '',
        nextVisitDate: m.nextVisitDate || '',
        interestedServices: m.interestedServices || []
      });
      const hasUpi = (m.services || []).some(s => s.slug === 'upi_qr');
      setShowUPIBanner(hasUpi);
    } catch (err) {
      console.error(err);
      showToast('Failed to load merchant');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const upiService = services.find(s => s.slug === 'upi_qr');
  const caService = services.find(s => s.slug === 'current_savings');
  const isUPISelected = upiService && form.interestedServices.includes(upiService.id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/merchants/${id}`, form);
      showToast('Merchant updated successfully');
      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleServiceToggle = (serviceId, slug) => {
    let newSelected = [...form.interestedServices];
    if (newSelected.includes(serviceId)) {
      newSelected = newSelected.filter(sid => sid !== serviceId);
      if (slug === 'upi_qr') {
        if (caService) newSelected = newSelected.filter(sid => sid !== caService.id);
        setShowUPIBanner(false);
      }
    } else {
      newSelected.push(serviceId);
      if (slug === 'upi_qr') {
        if (caService && !newSelected.includes(caService.id)) newSelected.push(caService.id);
        setShowUPIBanner(true);
      }
    }
    setForm(p => ({ ...p, interestedServices: newSelected }));
  };

  const openMaps = () => {
    if (merchant?.gps_lat && merchant?.gps_lng) {
      window.open(`https://www.google.com/maps?q=${merchant.gps_lat},${merchant.gps_lng}`, '_blank');
    }
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="au-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
          <div style={{ color: 'var(--navy)', fontFamily: 'var(--fh)' }}>Loading…</div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="au-page">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <button className="back-btn" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div style={{ fontFamily: 'var(--fh)', fontWeight: 700, color: 'var(--navy)', fontSize: '17px' }}>Edit Merchant</div>
            <div style={{ fontSize: '12px', color: 'var(--g3)' }}>{merchant?.businessName}</div>
          </div>
        </div>

        {/* GPS Button */}
        {merchant?.gps_lat && (
          <button className="gps-banner" style={{ marginBottom: '16px', width: '100%' }} onClick={openMaps} data-testid="gps-view-btn">
            <Navigation size={16} />
            <span>View Location on Maps</span>
          </button>
        )}

        {/* Form - Same layout as AddMerchantSheet */}
        <div className="form-card">
          <form onSubmit={handleSubmit}>
            <div className="fg">
              <label className="fl">Business Name <span className="req">*</span></label>
              <input className="fi" value={form.businessName}
                onChange={e => setForm(p => ({ ...p, businessName: e.target.value }))}
                required data-testid="edit-business-name" />
            </div>
            <div className="fg">
              <label className="fl">Owner Name <span className="req">*</span></label>
              <input className="fi" value={form.ownerName}
                onChange={e => setForm(p => ({ ...p, ownerName: e.target.value }))}
                required data-testid="edit-owner-name" />
            </div>
            <div className="fg">
              <label className="fl">Phone <span className="req">*</span></label>
              <input className="fi" value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                required inputMode="numeric" data-testid="edit-phone" />
            </div>
            <div className="fg">
              <label className="fl">Reschedule Next Visit</label>
              <input className="fi" type="date" value={form.nextVisitDate}
                onChange={e => setForm(p => ({ ...p, nextVisitDate: e.target.value }))}
                data-testid="edit-followup-date" />
            </div>
            <div className="frow fg">
              <div>
                <label className="fl">Street / Area</label>
                <input className="fi" value={form.address}
                  onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
              </div>
              <div>
                <label className="fl">City</label>
                <input className="fi" value={form.city}
                  onChange={e => setForm(p => ({ ...p, city: e.target.value }))} />
              </div>
            </div>

            <div className="fg">
              <label className="fl">Interested Services</label>
              {showUPIBanner && (
                <div className="sb-ban show">
                  <Smartphone size={16} />
                  <span>UPI/QR selected — <strong>Current/Savings Account</strong> auto-included</span>
                </div>
              )}
              <div className="cbg">
                {services.map(s => {
                  const isSelected = form.interestedServices.includes(s.id);
                  const isPrimary = s.slug === 'upi_qr';
                  const isAutoAdded = isUPISelected && s.slug === 'current_savings';
                  const icon = SERVICE_ICONS[s.slug] || '📋';
                  return (
                    <label
                      key={s.id}
                      className={`cbi ${isSelected ? 'on' : ''} ${isPrimary ? 'sb' : ''}`}
                      data-testid={`edit-service-${s.slug}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => !isAutoAdded && handleServiceToggle(s.id, s.slug)}
                        disabled={isAutoAdded}
                      />
                      <span className="cbi-icon">{icon}</span>
                      <span className="cbl">
                        {s.name}
                        {isPrimary && <span style={{ color: 'var(--red)', fontSize: '9px', marginLeft: '4px' }}>★</span>}
                        {isAutoAdded && <span style={{ color: 'var(--g3)', fontSize: '9px', marginLeft: '4px' }}>(auto)</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <button type="submit" className="sub-btn" disabled={saving} data-testid="save-edit-btn">
              <Save size={14} style={{ marginRight: '6px', display: 'inline' }} />
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </form>
        </div>
      </div>

      <Toast msg={toast.msg} show={toast.show} />
    </AgentLayout>
  );
}
